import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { CourseCard } from "@/components/courses/CourseCard";
import { CourseFilters } from "@/components/courses/CourseFilters";
import { SkeletonHolographic } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCourses } from "@/hooks/useCourses";
import { GraduationCap } from "lucide-react";

const Courses = () => {
  const { t } = useLanguage();
  const { courses, loading, error, filters, setFilters } = useCourses();

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="container mx-auto px-6 pt-32 pb-20">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-holographic-cyan/10 border border-holographic-cyan/30">
              <GraduationCap className="w-12 h-12 text-holographic-cyan animate-pulse-glow" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="text-holographic">{t("courses.title")}</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t("courses.subtitle")}
          </p>
        </div>

        {/* Filters */}
        <div className="mb-12 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <CourseFilters filters={filters} onFiltersChange={setFilters} />
        </div>

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="glass-card p-8 max-w-md mx-auto">
              <p className="text-destructive text-lg">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-4">
                <SkeletonHolographic className="h-48 w-full" />
                <SkeletonHolographic className="h-6 w-3/4" />
                <SkeletonHolographic className="h-4 w-full" />
                <SkeletonHolographic className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        )}

        {/* Courses Grid */}
        {!loading && !error && courses.length > 0 && (
          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            {courses.map((course) => (
              <CourseCard key={course.id} {...course} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && courses.length === 0 && (
          <div className="text-center py-20 animate-fade-in">
            <div className="glass-card p-12 max-w-md mx-auto">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted/20 flex items-center justify-center">
                <GraduationCap className="w-12 h-12 text-muted-foreground" />
              </div>
              <h3 className="text-2xl font-bold mb-2 text-foreground">
                {t("courses.noCourses")}
              </h3>
              <p className="text-muted-foreground">
                {t("courses.loading")}
              </p>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Courses;
